import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Image, Sparkles, Eye, RefreshCw, FileCheck, Zap, Files } from 'lucide-react';
import { api } from '../../lib/api';

export function EvidenceListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [autoOcr, setAutoOcr] = useState(true); // 自動OCRのON/OFF

  const { data, isLoading } = useQuery({
    queryKey: ['evidence'],
    queryFn: () => api.get<{ data: any[] }>('/api/evidence'),
  });

  const ocrMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/evidence/${id}/ocr`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['evidence'] }),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    const uploadedIds: string[] = [];

    try {
      // 複数ファイルを順次アップロード
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length });

        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const response = await api.upload('/api/evidence/upload', formData);
          if (response && (response as any).evidence?.id) {
            uploadedIds.push((response as any).evidence.id);
          }
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
        }
      }

      // アップロード完了後にリストを更新
      await queryClient.invalidateQueries({ queryKey: ['evidence'] });

      // 自動OCRがONの場合、アップロードした全ファイルに対してOCR実行
      if (autoOcr && uploadedIds.length > 0) {
        for (const id of uploadedIds) {
          try {
            await api.post(`/api/evidence/${id}/ocr`);
          } catch (err) {
            console.error(`Failed to run OCR for ${id}:`, err);
          }
        }
        // OCR完了後にリストを再更新
        await queryClient.invalidateQueries({ queryKey: ['evidence'] });
      }
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success">完了</span>;
      case 'processing':
        return <span className="badge badge-info">処理中</span>;
      case 'failed':
        return <span className="badge badge-error">失敗</span>;
      default:
        return <span className="badge badge-warning">未処理</span>;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          証憑管理
        </h1>
        <div className="flex items-center gap-4">
          {/* 自動OCR ON/OFF トグル */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoOcr(!autoOcr)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoOcr ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={autoOcr ? '自動OCR: ON' : '自動OCR: OFF'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoOcr ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
              <Zap size={14} className={autoOcr ? 'text-yellow-500' : ''} />
              自動OCR
            </span>
          </div>

          {/* アップロードボタン */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            {uploading ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                {uploadProgress.total > 1 
                  ? `${uploadProgress.current}/${uploadProgress.total} アップロード中...`
                  : 'アップロード中...'}
              </>
            ) : (
              <>
                <Files size={18} />
                ファイルを選択（複数可）
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* 自動OCR説明 */}
      {autoOcr && (
        <div 
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
          style={{ backgroundColor: 'var(--info-bg, rgba(59, 130, 246, 0.1))', color: 'var(--accent-color)' }}
        >
          <Zap size={16} />
          自動OCRがONです。アップロードした証憑は自動的にAI-OCRで解析されます。
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Image size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>証憑がありません</p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
              「ファイルを選択」ボタンから領収書や請求書をアップロードしてください
            </p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              複数ファイルを同時に選択できます
            </p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ color: 'var(--text-primary)' }}>ファイル名</th>
                <th style={{ color: 'var(--text-primary)' }}>OCRステータス</th>
                <th style={{ color: 'var(--text-primary)' }}>抽出日付</th>
                <th style={{ color: 'var(--text-primary)' }}>抽出金額</th>
                <th style={{ color: 'var(--text-primary)' }}>取引先</th>
                <th style={{ color: 'var(--text-primary)' }}>仕訳</th>
                <th style={{ color: 'var(--text-primary)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map((evidence: any) => (
                <tr key={evidence.id}>
                  <td style={{ color: 'var(--text-primary)' }}>
                    <span className="truncate max-w-[200px] block" title={evidence.fileName}>
                      {evidence.fileName}
                    </span>
                  </td>
                  <td>{getStatusBadge(evidence.ocrStatus)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {evidence.extractedDate || '-'}
                  </td>
                  <td className="font-mono" style={{ color: 'var(--text-primary)' }}>
                    {evidence.extractedAmount 
                      ? `¥${parseFloat(evidence.extractedAmount).toLocaleString()}` 
                      : '-'}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {evidence.extractedVendor || '-'}
                  </td>
                  <td>
                    {evidence.linkedJournalId ? (
                      <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--success)' }}>
                        <FileCheck size={14} />
                        紐付済
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {/* OCR実行/再実行ボタン */}
                      {(evidence.ocrStatus === 'pending' || evidence.ocrStatus === 'failed') && (
                        <button
                          onClick={() => ocrMutation.mutate(evidence.id)}
                          disabled={ocrMutation.isPending}
                          className="flex items-center gap-1 px-2 py-1 rounded text-sm"
                          style={{ 
                            backgroundColor: evidence.ocrStatus === 'failed' 
                              ? 'var(--error)' 
                              : 'var(--accent-color)',
                            color: 'white'
                          }}
                          title={evidence.ocrStatus === 'failed' ? 'OCR再実行' : 'OCR実行'}
                        >
                          {ocrMutation.isPending ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : (
                            <Sparkles size={14} />
                          )}
                          {evidence.ocrStatus === 'failed' ? '再実行' : 'OCR'}
                        </button>
                      )}
                      
                      {/* 処理中表示 */}
                      {evidence.ocrStatus === 'processing' && (
                        <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <RefreshCw size={14} className="animate-spin" />
                          処理中...
                        </span>
                      )}
                      
                      {/* 詳細ボタン */}
                      <button
                        onClick={() => navigate(`/evidence/${evidence.id}`)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-sm border"
                        style={{ 
                          borderColor: 'var(--border-color)',
                          color: 'var(--text-primary)'
                        }}
                        title="詳細を表示"
                      >
                        <Eye size={14} />
                        詳細
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
